<template>
  <div>
    <label
      for="transition-duration"
      class="block text-sm leading-5 font-medium text-gray-700 mb-1 mt-4"
      >Animation options</label
    >
    <section class="bg-gray-100 p-4 mt-2 rounded-md">
      <!-- DURATION -->
      <label
        for="animation-duration"
        class="block text-sm leading-5 font-medium text-gray-700 mb-2 mt-4"
        >Animation duration <span class="text-gray-500">(pixels)</span></label
      >
      <v-select
        v-model="element.duration"
        :options="transitionDuration"
        class="mb-4 bg-white"
      ></v-select>

      <label
        for="animation-duration"
        class="block text-sm leading-5 font-medium text-gray-700 mb-2 mt-4"
        >Trigger position (0 — screen top; 1 — screen bottom)
        <span class="text-gray-500">(pixels)</span></label
      >
      <v-select
        v-model="element.animationHook"
        :options="triggerPositions"
        class="mb-4 bg-white"
      ></v-select>

      <!-- ADJUST START AND END POSITION -->
      <div class="flex flex-row justify-between mt-4 w-auto">
        <div class="w-1/3">
          <label
            for="ending position"
            class="mt-2 block text-sm leading-5 font-medium text-gray-700 mb-2"
            >Starting position (%)</label
          >
          <div class="flex flex-row">
            <circle-slider
              progress-color="#3B82F6"
              circle-color="#3B82F6"
              knob-color="#3B82F6"
              v-model="element.startPosition"
            ></circle-slider>
            <span class="mt-6 text-4xl text-blue-500 ml-4">
              {{ element.startPosition }}
            </span>
          </div>
        </div>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="h-12 text-blue-400 mt-16 -ml-24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M14 5l7 7m0 0l-7 7m7-7H3"
          />
        </svg>

        <div class="w-1/3">
          <label
            for="ending position"
            class="mt-2 block text-sm leading-5 font-medium text-gray-700 mb-2"
            >Ending position (%)</label
          >
          <div class="flex flex-row">
            <circle-slider
              progress-color="#1D4ED8"
              circle-color="#1D4ED8"
              knob-color="#1D4ED8"
              v-model="element.endPosition"
            ></circle-slider>
            <span class="mt-6 text-4xl text-blue-700 ml-4">
              {{ element.endPosition }}
            </span>
          </div>
        </div>
      </div>

      <!-- ADJUST START AND END OPACITY -->
      <div class="flex flex-row justify-between mt-4">
        <div class="w-1/3">
          <label
            for="ending position"
            class="mt-2 block text-sm leading-5 font-medium text-gray-700 mb-2"
            >Starting opacity (%)</label
          >
          <div class="flex flex-row">
            <circle-slider
              progress-color="#9CA3AF"
              circle-color="#9CA3AF"
              knob-color="#9CA3AF"
              v-model="element.startOpacity"
            ></circle-slider>
            <span class="mt-6 text-4xl text-gray-400 ml-4">
              {{ element.startOpacity }}
            </span>
          </div>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="h-12 text-gray-400 mt-16 -ml-24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M14 5l7 7m0 0l-7 7m7-7H3"
          />
        </svg>
        <div class="w-1/3">
          <label
            for="ending position"
            class="mt-2 block text-sm leading-5 font-medium text-gray-700 mb-2"
            >Ending opacity (%)</label
          >
          <div class="flex flex-row">
            <circle-slider
              progress-color="#4B5563"
              circle-color="#4B5563"
              knob-color="#4B5563"
              v-model="element.endOpacity"
            ></circle-slider>
            <span class="mt-6 text-4xl text-gray-600 ml-4">
              {{ element.endOpacity }}
            </span>
          </div>
        </div>
      </div>

      <!-- ADJUST START AND END SCALE -->
      <div class="flex flex-row justify-between mt-4">
        <div class="w-1/3">
          <label
            for="ending position"
            class="mt-2 block text-sm leading-5 font-medium text-gray-700 mb-2"
            >Starting scale</label
          >
          <div class="flex flex-row">
            <circle-slider
              progress-color="#A78BFA"
              circle-color="#A78BFA"
              knob-color="#A78BFA"
              v-model="element.startScale"
              :max="10"
              :stepSize="0.1"
            ></circle-slider>
            <span class="mt-6 text-4xl text-purple-400 ml-4">
              {{ parseFloat(element.startScale).toFixed(2) }}
            </span>
          </div>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="h-12 text-purple-400 mt-16 -ml-24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M14 5l7 7m0 0l-7 7m7-7H3"
          />
        </svg>
        <div class="w-1/3">
          <label
            for="ending position"
            class="mt-2 block text-sm leading-5 font-medium text-gray-700 mb-2"
            >Ending scale</label
          >
          <div class="flex flex-row">
            <circle-slider
              progress-color="#7C3AED"
              circle-color="#7C3AED"
              knob-color="#7C3AED"
              :max="10"
              :stepSize="0.1"
              v-model="element.endScale"
            ></circle-slider>
            <span class="mt-6 text-4xl text-purple-600 ml-4">
              {{ parseFloat(element.endScale).toFixed(2) }}
            </span>
          </div>
        </div>
      </div>

      <!-- ADJUST START AND END WIDTH -->
      <div class="flex flex-row justify-between mt-4">
        <div class="w-1/3">
          <label
            for="ending position"
            class="mt-2 block text-sm leading-5 font-medium text-gray-700 mb-2"
            >Starting width (%)</label
          >
          <div class="flex flex-row">
            <circle-slider
              progress-color="#EF4444"
              circle-color="#EF4444"
              knob-color="#EF4444"
              v-model="element.startWidth"
              :max="100"
              :stepSize="1"
            ></circle-slider>
            <span class="mt-6 text-4xl text-red-500 ml-4">
              {{ parseFloat(element.startWidth).toFixed(2) }}
            </span>
          </div>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="h-12 text-red-500 mt-16 -ml-24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M14 5l7 7m0 0l-7 7m7-7H3"
          />
        </svg>
        <div class="w-1/3">
          <label
            for="ending position"
            class="mt-2 block text-sm leading-5 font-medium text-gray-700 mb-2"
            >Ending width (%)</label
          >
          <div class="flex flex-row">
            <circle-slider
              progress-color="#B91C1C"
              circle-color="#B91C1C"
              knob-color="#B91C1C"
              :max="100"
              :stepSize="1.0"
              v-model="element.endWidth"
            ></circle-slider>
            <span class="mt-6 text-4xl text-red-600 ml-4">
              {{ parseFloat(element.endWidth).toFixed(2) }}
            </span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
// import Component from "../component_location"

export default {
  components: {},

  props: ["element"],

  data() {
    return {
      transitionDuration: [100, 200, 300, 400, 500, 600, 700, 800],
      triggerPositions: [
        0.1,
        0.15,
        0.2,
        0.25,
        0.3,
        0.35,
        0.45,
        0.50,
        0.55,
        0.6,
        0.65,
        0.75,
        0.8,
        0.85,
        0.9,
        0.95,
      ],
    };
  },

  watch: {},

  methods: {},

  computed: {},

  mounted() {},
};
</script>

<style></style>
